// One place that answers "which icon and which colour does this place type get".
//
// 86cb7hz6z / 86cavfhec. Before this, four resolvers disagreed: `getPlaceType` (utils/places.ts),
// `getChatPoiPlaceType` (components/ai), and a byte-mirrored `SERPER_TYPE_CATEGORY_MAP` in both
// utils/serperPlace.ts and routes/api/services/serperService.ts. The same place got a different
// badge, colour and icon depending on which surface you were on.
//
// Three layers, in this order — the first that answers wins:
//
//   1. exact mapping — a hand-made decision per type            (typeIconMap.json)
//   2. word rule     — catches types we have never seen          (typeIconRules.json)
//   3. our own category — every place has one, so this never fails
//
// Measured over the full production log (2,212 types, 235,817 observations): layer 2 alone catches
// 99.4%. That is why a new country — or a type the AI chat invents — needs no new manual round.
//
// This file is the source of truth for BOTH runtimes: the backend reaches it through the
// `routes/api/services/placeTypes` symlink, the same trick pdfexport uses. Keep it free of
// frontend-only imports (no path aliases, no `constants/place`) or the backend build breaks.
import MAP from "./typeIconMap.json";
import RULES from "./typeIconRules.json";

export type PlaceTypeSource =
	| "serper"
	| "chat" // the AI chat invents types outside its own vocabulary: lodge, waterfall, safari
	| "nuitee"
	| "app-attraction"
	| "stippl-category"
	| "stippl-subtype"
	| "stippl-destination";

// Sources that hand us free text rather than a fixed vocabulary. The word rules are only
// meaningful there — and are only allowed to run there.
const FREE_TEXT: ReadonlyArray<PlaceTypeSource> = ["serper", "chat"];

const compiledRules = RULES.rules.map((rule) => ({ re: new RegExp(rule.pattern, "i"), icon: rule.icon }));

// Layer 3: what an unknown type falls back to. Every place has a category, so the chain always
// ends on a real icon.
const ICON_BY_CATEGORY: Record<string, string> = {
	hotel: "bed",
	restaurant: "restaurant",
	cafe: "coffee",
	attraction: "landmark",
	bar: "cocktail",
	experience: "adventure",
	// the live app taxonomy
	accommodation: "bed",
	cuisine: "cuisine",
	activity: "see&do",
	transport: "transport--outlined",
	destination: "destination",
	article: "article",
};
// Deliberately not a real category: "we do not know" must not present itself as see&do.
// See the CATEGORY_BY_ICON entry for `marker` below.
const LAST_RESORT = "marker";

// The eight categories the mapping is authored against. We do NOT render these directly — see
// PLACE_BUCKET_BY_CATEGORY below for why. They exist because they are what the type data actually
// distinguishes, and collapsing at the edge keeps that detail available if we ever want it.
export type PlaceTypeCategory =
	| "eat&drink"
	| "stay"
	| "see&do"
	| "outdoors"
	| "shopping"
	| "transport"
	| "destination" // a different zoom level: where you travel to, not what you visit there
	| "practical";

// The six buckets the app actually persists and colours by — the string values of `ATTRACTIONS`
// (constants/place.ts), repeated here rather than imported so the backend can use this file too.
export type PlaceBucket = "accommodation" | "activity" | "cuisine" | "transport" | "destination" | "article";

// The eight collapse onto the six. Deliberate, and deliberately lossy.
//
// `attraction` is persisted on Place documents and lives in the GraphQL schema shared with mobile,
// and the four `$main1..4` colours are semantic bucket colours with derived shadow and filter
// assets across web, mobile, embed and PDF export. Three more categories would mean three more
// colour identities in every one of those places — and presentation mode collapses everything to
// `--brand-secondary` anyway, so B2B share, PDF and embeds would never show the difference.
//
// So we take the icons at full fidelity (231 distinct icons, up from 35) and the buckets collapsed.
// A supermarket gets the shopping icon and the activity colour; a beach and a museum keep the same
// colour but stop sharing a pin. Both are strictly better than before, neither is full fidelity.
const PLACE_BUCKET_BY_CATEGORY: Record<PlaceTypeCategory, PlaceBucket> = {
	"eat&drink": "cuisine",
	stay: "accommodation",
	"see&do": "activity",
	outdoors: "activity",
	shopping: "activity",
	transport: "transport",
	destination: "destination",
	practical: "activity",
};

type MapRow = { icon: string | null; icons8Name: string | null; category: string | null };

// Layer 1 in one place, so resolvePlaceIcon() and resolvePlaceType() can never find different rows.
function lookUp(source: PlaceTypeSource, type?: string | null): MapRow | null {
	if (!type) return null;
	const rows = MAP.mapping as Record<string, MapRow>;
	const own = rows[`${source}:${type}`];
	if (own) return own;
	// Google-style types arrive underscored (`art_museum`, `grocery_or_supermarket`) while the
	// mapping is authored in the spaced form Serper's production log uses. Try the exact key first —
	// `administrative_area_level_1` and friends really are underscored rows — then the spaced form.
	if (type.includes("_")) {
		const spaced = rows[`${source}:${type.replace(/_/g, " ")}`];
		if (spaced) return spaced;
	}
	// The chat names the same kinds of places Serper does, so those hand-made decisions apply there
	// too. This is exactly why `stippl-destination` stays a separate source and does NOT inherit:
	// `serper:island` is a place you visit, `stippl-destination:Island` is a destination.
	if (source === "chat") return lookUp("serper", type);
	return null;
}

// Safety net for an icon reached by a word rule rather than by a mapping row.
//
// 18 icons are used by more than one meaning — `beach` covers both a beach and a beach club. That
// is not a fault: a mapping row carries its own category and never comes past here. This table only
// applies to layer 2, so the criterion is: **what does the word RULE landing on this icon mean?**
// Not: which meaning is most common in the mapping.
const CATEGORY_BY_ICON: Record<string, PlaceTypeCategory> = {
	adventure: "see&do",
	apartment: "stay",
	// Deliberately outdoors: the rule landing here is `aquarium|oceanarium`.
	// `fish and chips restaurant` is its own row in the mapping and carries its
	// own eat&drink there, so the two do not collide.
	fish: "outdoors",
	article: "practical",
	arts: "see&do",
	asian: "eat&drink",
	bakery: "eat&drink",
	bbq: "eat&drink",
	// the rule is `beach|seaside|shore|coast` — a beach, not a beach bar
	beach: "outdoors",
	bed: "stay",
	"bed&breakfast": "stay",
	beer: "eat&drink",
	bicycle: "transport",
	boat: "transport",
	boatIcon: "transport",
	bridge: "see&do",
	brunch: "eat&drink",
	budget: "stay",
	// the rule is `\bbus\b|minibus|coach station` — transport, not the bus tour
	bus: "transport",
	// the rule covers offices and convention centres, not hospitality
	business: "practical",
	campervan: "stay",
	camping: "stay",
	car: "transport",
	card: "practical",
	carryon: "practical",
	cash: "practical",
	cave: "outdoors",
	charging: "practical",
	church: "see&do",
	city: "destination",
	"city-church": "see&do",
	clothing: "shopping",
	// the rule is `night ?club|disco|dance club` — going out, not eating
	club: "see&do",
	cocktail: "eat&drink",
	coffee: "eat&drink",
	crossfit: "see&do",
	cruise: "transport",
	cuisine: "eat&drink",
	culinary: "eat&drink",
	culture: "see&do",
	cycling: "outdoors",
	destination: "destination",
	dinner: "eat&drink",
	dumbbell: "see&do",
	eatdrink: "eat&drink",
	electronics: "shopping",
	entertainment: "see&do",
	esim: "practical",
	essentials: "shopping",
	exchange: "practical",
	fastfood: "eat&drink",
	festival: "see&do",
	finedining: "eat&drink",
	gallery: "see&do",
	gas: "transport",
	gift: "shopping",
	globe: "destination",
	groceryBag: "shopping",
	gym: "see&do",
	help: "practical",
	heritageSite: "see&do",
	hike: "outdoors",
	hiking: "outdoors",
	historic: "see&do",
	pill: "practical",
	icecream: "eat&drink",
	identification: "practical",
	info: "practical",
	internet: "practical",
	// an unknown Serper type is a place to visit; `stippl-destination:Island` carries destination itself
	island: "outdoors",
	italian: "eat&drink",
	kitesurfing: "outdoors",
	landmark: "see&do",
	laundry: "practical",
	makeup: "shopping",
	marker: "practical", // unknown — neutral, deliberately not see&do
	// Deliberately shopping: the word rule landing here is `\bmarkets?\b|bazaar|souk`,
	// which is more often a retail market than a food market. The food markets in the
	// mapping carry their own category, so this does not touch them.
	"market-square": "shopping",
	mexican: "eat&drink",
	money: "practical",
	mosque: "see&do",
	motorcycle: "transport",
	mountainrange: "outdoors",
	movie: "see&do",
	// the rule is `museum`; the museum shop is its own row
	museum: "see&do",
	musicfestival: "see&do",
	// same again: a place as a Serper type, a destination as a destination type
	nationalPark: "outdoors",
	nationalpark: "outdoors",
	naturalFeature: "outdoors",
	nature: "outdoors",
	nightlife: "see&do",
	obelisk: "see&do",
	// Deliberately practical: the rule is `business cent|convention cent|…|office`.
	// The furniture shops sharing this icon are their own rows, categorised shopping.
	"office-chair-2": "practical",
	packing: "practical",
	park: "outdoors",
	// the rule is `parking|car park`; the car-park park is its own row
	parking: "transport",
	peoples: "see&do",
	photography: "see&do",
	plane: "transport",
	planet: "destination",
	play: "see&do",
	playground: "outdoors",
	region: "destination",
	rentalcar: "transport",
	resort: "stay",
	restaurant: "eat&drink",
	romantic: "see&do",
	ruins: "see&do",
	running: "see&do",
	sailboat: "transport",
	scenic: "outdoors",
	scooter: "transport",
	"scuba-mask": "see&do",
	seafood: "eat&drink",
	"see&do": "see&do",
	shopping: "shopping",
	simcard: "practical",
	ski: "outdoors",
	// the rule is anchored on `^ski resort$`; skiing is outdoors
	skiResort: "outdoors",
	sleep: "stay",
	spiritual: "see&do",
	// the rule is `golf|tennis|stadium|\bsports?\b`; the sports bar is its own row
	sports: "see&do",
	// flea market and floating market outweigh the night market
	stall: "shopping",
	star: "see&do",
	subway: "transport",
	supermarket: "shopping",
	sweets: "eat&drink",
	// the rule is `swim|pool|water ?park`; the indoor pool is its own row
	swimming: "outdoors",
	taxi: "transport",
	theatre: "see&do",
	ticket: "see&do",
	toiletries: "shopping",
	tour: "see&do",
	// the rule is `^town$|^village$|village|hamlet` — all destinations
	town: "destination",
	train: "transport",
	tram: "transport",
	"transport--outlined": "transport",
	transportMarker: "transport",
	trekkin: "outdoors",
	userGroup: "see&do",
	vegan: "eat&drink",
	// the rule is `villa|bungalow|chalet|cottage|cabin` — all places to sleep
	villa: "stay",
	village: "destination",
	visas: "practical",
	walk: "outdoors",
	waterfall: "outdoors",
	watersports: "outdoors",
	// the rule is `spa|massage|sauna|wellness`; the retreat hotel is its own row
	wellness: "see&do",
	wildlife: "outdoors",
	wine: "eat&drink",
	wintersports: "outdoors",
	world: "destination",
};

function derivedCategory(icon: string): PlaceTypeCategory {
	return CATEGORY_BY_ICON[icon] ?? "see&do";
}

export type ResolveInput = {
	source: PlaceTypeSource;
	type?: string | null;
	/** our own category, for layer 3 — every place has one, so the chain always terminates */
	category?: string | null;
};

/**
 * Which layer answered. Callers that must tell a real decision apart from a shrug need this:
 * `"category"` and `"none"` mean we did NOT recognise the type, and treating them as if we had is
 * how a German restaurant ends up badged as an activity — one wrong badge traded for another.
 */
export type MatchedLayer = "map" | "rule" | "category" | "none";

export const isRecognised = (matched: MatchedLayer): boolean => matched === "map" || matched === "rule";

// Exported alongside `resolvePlaceIcon` because callers that need to know WHICH layer matched
// (chatPoiPlaceType's provider-truth precedence) cannot get that from the icon string alone.
export function resolveIconWithLayer(input: ResolveInput): { icon: string; matched: MatchedLayer } {
	const { source, type, category } = input;

	if (type) {
		// 1 — exact
		const row = lookUp(source, type);
		if (row) return { icon: row.icon ?? row.icons8Name ?? LAST_RESORT, matched: "map" };

		// 2 — word rule
		if (FREE_TEXT.includes(source)) {
			for (const rule of compiledRules) {
				if (rule.re.test(type)) {
					if (rule.icon) return { icon: rule.icon.startsWith("i8:") ? rule.icon.slice(3) : rule.icon, matched: "rule" };
					break; // rule matched but deliberately has no icon -> fall through to layer 3
				}
			}
		}
	}

	// 3 — category
	if (category && ICON_BY_CATEGORY[category]) return { icon: ICON_BY_CATEGORY[category], matched: "category" };
	return { icon: LAST_RESORT, matched: "none" };
}

/** The icon name. Always a real key in components/Icons/Icons.json. */
export function resolvePlaceIcon(input: ResolveInput): string {
	return resolveIconWithLayer(input).icon;
}

/** Icon plus the eight-value category the mapping is authored against, and which layer answered. */
export function resolvePlaceTypeCategory(input: ResolveInput): { icon: string; category: PlaceTypeCategory; matched: MatchedLayer } {
	const { icon, matched } = resolveIconWithLayer(input);
	const row = lookUp(input.source, input.type);
	return { icon, category: (row?.category as PlaceTypeCategory) ?? derivedCategory(icon), matched };
}

/**
 * Icon plus the six-value bucket the app persists and colours by — the call site most code wants.
 * `bucket` is safe to compare against `ATTRACTIONS` on the frontend; the values are identical.
 */
export function resolvePlaceType(input: ResolveInput): { icon: string; category: PlaceTypeCategory; bucket: PlaceBucket; matched: MatchedLayer } {
	const { icon, category, matched } = resolvePlaceTypeCategory(input);
	return { icon, category, bucket: PLACE_BUCKET_BY_CATEGORY[category], matched };
}

/** Exported for tests and for callers that already hold a category. */
export const toPlaceBucket = (category: PlaceTypeCategory): PlaceBucket => PLACE_BUCKET_BY_CATEGORY[category];

// ---------------------------------------------------------------------------------------------
// Serper category resolution.
//
// 86cb7hz6z. This used to live twice — byte-mirrored between `frontend/src/utils/serperPlace.ts`
// and `routes/api/services/serperService.ts`, with "keep both in sync" comments and a test parsing
// both files as text to enforce it. That is how the `bar` icon bug survived from Dec 2025: the
// backend was fixed, the frontend copy was missed, and nothing failed. Both now import from here.
// ---------------------------------------------------------------------------------------------

/** One icon per bucket — the pre-86cavfhec vocabulary, kept as the floor when nothing is recognised. */
export const BUCKET_ICONS: Record<string, string> = {
	accommodation: "sleep",
	cuisine: "eatdrink",
	activity: "see&do",
	transport: "car",
	destination: "destination",
};

const lower = (value?: string | null) => (value ? String(value).toLowerCase() : "");

// Unicode-safe stand-in for `\b`. Plain `\b` is ASCII-only and treats `_` as a word character, so it
// doesn't reliably bound multi-word Serper type strings and silently fails on accented type names
// (e.g. "café"). Requires a non-letter/non-number — or the start/end of the string — on both sides.
// This is what stops "eat" matching inside "theatre" and "bus" inside "business" (86cavfhec).
const wordBoundary = (alternatives: string): RegExp => new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives})(?![\\p{L}\\p{N}])`, "u");

/**
 * The bucket for a Serper type or category, or undefined when we genuinely do not recognise it.
 *
 * Callers need that undefined: Serper's `category` is LOCALIZED display text ("Italiaans
 * restaurant", "Gaststätte"), so an unrecognised value means "we do not know", not "activity", and
 * asserting a bucket there just trades one wrong badge for another.
 */
export const inferCategoryFromValue = (value?: string | null): PlaceBucket | undefined => {
	const normalised = lower(value);
	if (!normalised) return undefined;

	// Layer 1: the mapping's exact, hand-made decisions — an EXACT match only. The word rules are
	// deliberately NOT consulted here. They are loose regexes meant to pick an icon, and on
	// localized text they produce confident nonsense: a plain `gas` alternative matched "Gaststätte"
	// and answered transport. The boundary-checked ladder below is the curated, telemetry-informed
	// matcher and stays the only fuzzy layer in this function.
	const exact = resolvePlaceType({ source: "serper", type: normalised });
	if (exact.matched === "map") return exact.bucket;

	// "lodg" is a deliberate stem (lodge/lodging/lodgings), so unlike everything below it only gets
	// a left-boundary check rather than a whole-word match.
	if (/(?<![\p{L}\p{N}])lodg/u.test(normalised)) return "accommodation";
	if (wordBoundary("hotel|motel|resort|bnb|apartment|hostel|guesthouse|guest.house|villa|cottage|rental|bed and breakfast|homestay").test(normalised))
		return "accommodation";
	if (wordBoundary("restaurant|food|dining|eat|drink|bar|cafe|bakery|coffee").test(normalised)) return "cuisine";
	if (wordBoundary("airport|station|transit|transport|bus|train|taxi|tram|parking|car_rental|car\\s*rental|ferry|harbor").test(normalised)) return "transport";
	if (wordBoundary("tour|museum|gallery|attraction|park|activity|theatre|stadium|point_of_interest").test(normalised)) return "activity";
	// Intentionally does NOT include bare "city"/"town"/"village"/"country" — in production telemetry
	// (SerperTypeLog) those four fragments accounted for 100% of "destination" hits, and every one of
	// them (town square, city hall, country club, city courthouse, ...) turned out to be a building
	// or institution rather than a place you travel to. See 86cavfhec.
	if (wordBoundary("locality|political|administrative_area").test(normalised)) return "destination";
	return undefined;
};

/**
 * Bucket plus icon for a place. The icon comes from the full three-layer resolver — 231 distinct
 * icons where the old local table had 35 — so a beach, a volcano and a museum stop sharing a pin.
 */
export const applyCategory = (category: PlaceBucket, type?: string) => {
	const icon = type ? resolvePlaceIcon({ source: "serper", type: lower(type), category }) : undefined;
	// `marker` is the resolver saying it recognised nothing. Persist the bucket's own icon instead of
	// a pin that reads as blank — the behaviour this had before.
	return { attraction: category, icon: icon && icon !== LAST_RESORT ? icon : BUCKET_ICONS[category] || BUCKET_ICONS.activity };
};

/** Tries the main type, then the fallback list, then defaults to activity. */
export const mapSerperTypeToCategory = (type?: string, fallbackTypes: string[] = []) => {
	for (const candidate of [type, ...fallbackTypes]) {
		const derived = inferCategoryFromValue(candidate);
		if (derived) return applyCategory(derived, candidate);
	}
	// 86cam4b60 / 86cavfhec — frontend and backend must default identically. This used to default to
	// "destination" on the frontend and "activity" on the backend, so the same unmatched type
	// rendered differently depending on whether it came from a persisted Place or a client preview.
	return applyCategory("activity", type);
};
